'use client';

import { useState } from 'react';
import { useClientTranslation, useClientLocale } from '@bts-web/utils-lokalise';
import { intlFiatValueFormatting } from '@bts-web/utils-formatting';
import {
  ButtonBase,
  Collapsible,
  BelowInputInfoTable,
  NavigationBar,
} from '../../../../common';
import { getAppConfig, CURRENCY } from '@bts-web/core-app-config';
import { getWithdrawalSummaryTranslations } from './getWithdrawalSummaryTranslations';
import {
  FilledSpinnerIos,
  RegularArrowLeft,
  RegularEuro,
} from '../../../../common/svgIcons';
import { CloseDrawerButton } from '../../../../common/components/Drawer/CloseDrawerButton';
import {
  wrapperStyles,
  bodyStyles,
  contentStyles,
  bottomSectionStyles,
} from '../../../../common/styles';
import { TwoIconsWithText } from '../../../Reusable/TwoIconsWithText/TwoIconsWithText';
import { PoweredByTextWithLogo } from '../../../Reusable/PoweredBy/PoweredBy';
import { useDrawerMetadata } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { WithdrawalFeesBreakdown } from '../Reusable/WithdrawalFeesBreakdown';

// TODO: replace AED and GBP with their respective currency icons once available in the icon library
const CURRENCY_ICON_MAP: Record<CURRENCY, typeof RegularEuro> = {
  EUR: RegularEuro,
  AED: RegularEuro,
  GBP: RegularEuro,
};

export interface WithdrawalSummaryContentProps {
  amount: string;
  payoutAccountId: string;
  recipientName: string;
  bankName: string;
  accountNumber: string;
  fee: string | null;
  onBack?: () => void;
  onConfirm: (params: {
    amount: string;
    payoutAccountId: string;
    recipientName: string;
  }) => void;
}

export const WithdrawalSummaryContent = ({
  amount,
  payoutAccountId,
  recipientName,
  bankName,
  accountNumber,
  fee,
  onBack,
  onConfirm,
}: WithdrawalSummaryContentProps) => {
  const { t } = useClientTranslation();

  const translations = getWithdrawalSummaryTranslations(t);

  const { bankLogo: BankLogo, currency } = getAppConfig();

  const locale = useClientLocale();

  const { closeThisDrawer } = useDrawerMetadata();

  const formattedAmount = intlFiatValueFormatting(amount, { locale, currency });

  const CurrencyIcon = CURRENCY_ICON_MAP[currency];

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = () => {
    setIsSubmitting(true);

    onConfirm({ amount, payoutAccountId, recipientName });
  };

  const bankDetailsItems = [
    {
      title: translations.bankAccountHolder,
      value: recipientName,
    },
    {
      title: translations.accountNumber,
      value: accountNumber,
    },
    {
      title: translations.bankName,
      value: bankName,
    },
    {
      title: translations.memo,
      value: translations.memoDetails,
    },
  ];

  return (
    <div data-testid="withdrawal-summary-content" className={wrapperStyles}>
      <NavigationBar
        title={translations.summary}
        NavigationLeftSlot={
          <ButtonBase
            onClick={onBack ?? closeThisDrawer}
            visual="ghost"
            size="small"
            aria-label="navigation back button"
            data-testid="navigation-back-button"
          >
            <RegularArrowLeft size={24} data-dir="rtl" />
          </ButtonBase>
        }
        NavigationRightSlot={<CloseDrawerButton />}
      />

      <div className={bodyStyles}>
        <div className={contentStyles}>
          <TwoIconsWithText
            leftIcon={
              <CurrencyIcon size={56} color="neutrals.text_primary_inverted" />
            }
            leftIconStyles={{ backgroundColor: 'neutrals.fill_primary' }}
            rightIcon={BankLogo ? <BankLogo size={56} /> : undefined}
            title={formattedAmount}
            subtitle={t('to_recipient', { name: recipientName })}
            subtitleStyles={{ mb: '0' }}
          />

          <Collapsible
            header={{
              LeftContentIndicatorSlot: translations.myBankAccountDetails,
            }}
          >
            <BelowInputInfoTable items={bankDetailsItems} />
          </Collapsible>

          {fee && <WithdrawalFeesBreakdown amount={amount} fee={fee} />}
        </div>

        <div className={bottomSectionStyles}>
          <PoweredByTextWithLogo noMargins />

          <ButtonBase
            data-testid="withdrawal-confirm-button"
            visual="accent"
            size="large"
            onClick={handleConfirm}
            disabled={isSubmitting}
            fullWidth
            noHover
          >
            {translations.confirm}
            {isSubmitting && (
              <FilledSpinnerIos
                aria-label="loading icon"
                data-element="loading-icon"
              />
            )}
          </ButtonBase>
        </div>
      </div>
    </div>
  );
};
